import React, { useState } from "react";
import { addToCollection, getAllCollection } from "../services/database";
import uuid from "react-uuid";
import styled from "styled-components";

const Container = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: auto;
  padding: 10px;
  border-radius: 10px;
  box-shadow: 0px 4px 10px rgba(0, 0, 0, 0.1);
  background: #fff;
`;

const Title = styled.h2`
  text-align: center;
  color: #333;
  margin-bottom: 20px;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Input = styled.input`
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 5px;
  font-size: 16px;
  outline: none;
  &:focus {
    border-color: #da2128;
  }
`;

const Textarea = styled.textarea`
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 5px;
  font-size: 14px;
  min-height: 180px;
  outline: none;
  &:focus {
    border-color: #da2128;
  }
`;

const Button = styled.button`
  background: #da2128;
  color: white;
  border: none;
  padding: 10px;
  border-radius: 5px;
  cursor: pointer;
  font-size: 16px;
  transition: 0.3s;
  display: flex;
  align-items: center;
  justify-content: center;
  &:hover {
    background: #b61b20;
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const Loader = styled.div`
  width: 20px;
  height: 20px;
  border: 3px solid white;
  border-top: 3px solid transparent;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const Message = styled.p`
  text-align: center;
  color: ${(p) => (p.error ? "crimson" : "green")};
  font-weight: bold;
  margin-top: 10px;
`;

function parseTopics(topicsRaw) {
  return (topicsRaw || "")
    .split(/\n/g)
    .map((t) => t.trim())
    .filter(Boolean);
}

function parseRows(rowsRaw) {
  const lines = (rowsRaw || "")
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean);

  return lines.map((line) => {
    const parts = line.split(",").map((p) => p.trim());

    return {
      name: parts[0] || "",
      email: parts[1] || "",
      company: parts[2] || "",
      spol: (parts[3] || "").toLowerCase(),
      certificateNumber: parts[4] || "",
    };
  });
}

function formatDateDMY(dateStr) {
  if (!dateStr) return "";
  const [year, month, day] = dateStr.split("-");
  return `${day}. ${month}. ${year}.`;
}

function makeCertificateNumber(number) {
  const year = 2026;
  return `AML-F-${String(number).padStart(5, "0")}/${year}`;
}

function getMaxForumNumber(existingCertificates) {
  let max = 99;

  existingCertificates.forEach((cert) => {
    const certNo = cert.certificateNumber || "";
    const match = certNo.match(/^AML-F-(\d{5})\/2026$/);

    if (match) {
      const num = Number(match[1]);
      if (num > max) max = num;
    }
  });

  return max;
}

const AddForumCertificates = () => {
  const [title, setTitle] = useState(
    "XVIII Forum „Sprečavanje pranja novca, finansiranja terorističkih aktivnosti i borba protiv korupcije“"
  );

  const [duration, setDuration] = useState("8 sati");
  const [trainingDate, setTrainingDate] = useState("");
  const [issuePlace, setIssuePlace] = useState("Sarajevo");
  const [issueDate, setIssueDate] = useState("");

  const [topicsRaw, setTopicsRaw] = useState(`ključni nalazi MONEYVAL evaluacije i prioriteti za unapređenje sistema SPNiFT
aktuelno stanje i planske aktivnosti institucija u borbi protiv pranja novca, korupcije i finansijskog kriminala
očekivani pravci razvoja okvira i sektorskih smjernica u oblasti SPNiFT
iskustva iz nadzora finansijskog i nefinansijskog sektora i njihov uticaj na dalje unapređenje praksi obveznika
ključne slabosti, rizici i sistemski izazovi u nefinansijskom sektoru
izazovi upravljanja rizicima kroz različite faze AML sistema u finansijskom sektoru
zaštita ličnih podataka i sigurnost podataka u sistemu SPNiFT
rizici povezani sa digitalnom imovinom, novim finansijskim modelima i digitalnim kanalima poslovanja
proliferacijski, sigurnosni i rizici povezani sa primjenom međunarodnih sankcija
primjeri iz prakse i preporuke za unapređenje sistema usklađenosti`);

  const [rowsRaw, setRowsRaw] = useState("");

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [errorMsg, setErrorMsg] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();

    setLoading(true);
    setMessage("");
    setErrorMsg("");

    const topics = parseTopics(topicsRaw);
    const rows = parseRows(rowsRaw);

    if (rows.length === 0) {
      setErrorMsg("Nema unosa. Dodaj bar jednu liniju.");
      setLoading(false);
      return;
    }

    const invalid = rows.filter((r) => !r.name || !r.email);

    if (invalid.length) {
      setErrorMsg("Neki redovi nemaju ime ili email. Provjeri unos.");
      setLoading(false);
      return;
    }

    try {
      const existingResp = await getAllCollection("forum-certificates");
      const existingForumCertificates = existingResp?.data || [];
      const maxExistingNumber = getMaxForumNumber(existingForumCertificates);

      const trainingDateFmt = formatDateDMY(trainingDate);
      const issueDateFmt = formatDateDMY(issueDate);

      const certificates = rows.map((r, index) => {
        const id = uuid();

        return {
          id,
          title,
          name: r.name,
          email: r.email,
          company: r.company || "",
          spol: r.spol || "",
          gender: r.spol || "",

          certificateNumber:
            r.certificateNumber ||
            makeCertificateNumber(maxExistingNumber + index + 1),

          duration: duration || "",
          trainingDate: trainingDateFmt || "27. i 28. 04. 2026.",
          issuePlace: issuePlace || "Sarajevo",
          issueDate: issueDateFmt || "28. 04. 2026.",

          topics,
          type: "forum",
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };
      });

      await Promise.all(
        certificates.map((cert) =>
          addToCollection("forum-certificates", cert.id, cert)
        )
      );

      const mailPayload = certificates.map((c) => ({
        id: c.id,
        email: c.email,
        name: c.name,
        title: c.title,
      }));

     let successCount = 0;
let failedCount = 0;
let failedEmails = [];

for (const item of mailPayload) {
  try {
    const resp = await fetch("https://revicon.info/api/send-forum-certificates", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify([item]),
    });

    if (resp.ok) {
      successCount++;
    } else {
      failedCount++;
      failedEmails.push(item.email);
    }
  } catch (err) {
    failedCount++;
    failedEmails.push(item.email);
  }

  await new Promise((resolve) => setTimeout(resolve, 700));
}

if (failedCount > 0) {
  setErrorMsg(
    `Upis u bazu je OK. Poslano: ${successCount}, nije poslano: ${failedCount}. Problematični emailovi: ${failedEmails.join(", ")}`
  );
} else {
  setMessage(
    `Uspješno kreirano ${certificates.length} forum potvrda i poslani mailovi. Zadnji broj: ${
      certificates[certificates.length - 1].certificateNumber
    }`
  );
}

    } catch (err) {
      console.error(err);
      setErrorMsg("Greška prilikom upisa u bazu ili slanja mailova.");
    } finally {
      setLoading(false);
    }
  };
  return (
    <Container>
      <Title>Import forum potvrda</Title>

      <Form onSubmit={handleSubmit}>
        <Input
          type="text"
          placeholder="Naziv foruma"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          required
        />

        <Input
          type="text"
          placeholder="Trajanje, npr. 8 sati"
          value={duration}
          onChange={(e) => setDuration(e.target.value)}
        />

        <Input
          type="date"
          value={trainingDate}
          onChange={(e) => setTrainingDate(e.target.value)}
        />

        <Input
          type="text"
          placeholder="Mjesto izdavanja"
          value={issuePlace}
          onChange={(e) => setIssuePlace(e.target.value)}
        />

        <Input
          type="date"
          value={issueDate}
          onChange={(e) => setIssueDate(e.target.value)}
        />

        <Textarea
          placeholder="Teme foruma, svaka tema novi red"
          value={topicsRaw}
          onChange={(e) => setTopicsRaw(e.target.value)}
        />

        <Textarea
          placeholder={`Unos po linijama:
Ime Prezime, email, firma, spol m/f, brojPotvrde opcionalno

Primjer:
Dženita Mutap, dzenita@email.com, REVICON d.o.o. Sarajevo, f
John Smith, john@email.com, Company Ltd, m
Test User, test@email.com, Test Company, m, AML-F-00150/2026`}
          value={rowsRaw}
          onChange={(e) => setRowsRaw(e.target.value)}
          required
        />

        <Button type="submit" disabled={loading}>
          {loading ? <Loader /> : "Upiši forum potvrde + pošalji mailove"}
        </Button>
      </Form>

      {errorMsg && <Message error>{errorMsg}</Message>}
      {message && <Message>{message}</Message>}
    </Container>
  );
};

export default AddForumCertificates;